import React, { useState, useEffect, useCallback } from "react";
import {
    Users2,
    Users,
    ArrowLeft,
    Loader2,
    AlertCircle,
    Settings,
    UserPlus,
    Mail,
    X
} from "lucide-react";
import { fetchGroupMembers, addGroupMember, removeGroupMember } from "../../api/organizations";
import MembersList from "./MembersList";
import { isValidEmail, getInitials } from "../../utils/organizationHelpers";

const GroupDetail = ({
    organization,
    workspace,
    group,
    onBack,
    userEmail,
    canEdit = false
}) => {
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    // Add member state
    const [showAddMember, setShowAddMember] = useState(false);
    const [newMemberEmail, setNewMemberEmail] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const [addError, setAddError] = useState("");

    const loadData = useCallback(async () => {
        // Handle both id and *_id from backend
        const orgId = organization?.id || organization?.org_id;
        const wsId = workspace?.id || workspace?.workspace_id;
        const grpId = group?.id || group?.group_id;
        if (!orgId || !wsId || !grpId) return;

        setIsLoading(true);
        setError("");
        try {
            const response = await fetchGroupMembers(orgId, wsId, grpId);
            // Handle various response structures
            const membersData = response?.members || response?.data?.members || [];
            setMembers(Array.isArray(membersData) ? membersData : []);
        } catch (err) {
            console.error("Failed to load group data:", err);
            setError("Unable to load group data.");
        } finally {
            setIsLoading(false);
        }
    }, [organization?.id, organization?.org_id, workspace?.id, workspace?.workspace_id, group?.id, group?.group_id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleAddMember = async (e) => {
        e.preventDefault();
        const email = newMemberEmail.trim();

        if (!email) return;

        if (!isValidEmail(email)) {
            setAddError("Please enter a valid email address.");
            return;
        }

        if (members.some(m => (m.email || m.user_id || "").toLowerCase() === email.toLowerCase())) {
            setAddError("This user is already a member of this group.");
            return;
        }

        const orgId = organization?.id || organization?.org_id;
        const wsId = workspace?.id || workspace?.workspace_id;
        const grpId = group?.id || group?.group_id;

        setIsAdding(true);
        setAddError("");
        try {
            await addGroupMember(orgId, wsId, grpId, { email });
            setNewMemberEmail("");
            setShowAddMember(false);
            await loadData();
        } catch (err) {
            setAddError(err?.message || "Failed to add member to group.");
        } finally {
            setIsAdding(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-auto">
            {/* Header */}
            <div className="px-6 py-6 border-b dark:border-slate-800 border-slate-200">
                <div className="max-w-5xl mx-auto">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 text-sm dark:text-slate-400 text-slate-500 hover:text-teal-500 dark:hover:text-teal-400 mb-4"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to {workspace.name}
                    </button>
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                                <Users2 className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold dark:text-white text-slate-900">
                                    {group.name}
                                </h2>
                                {group.description && (
                                    <p className="dark:text-slate-400 text-slate-500 mt-1">
                                        {group.description}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            <span className="px-3 py-1.5 dark:bg-slate-800 bg-slate-100 rounded-lg dark:text-slate-300 text-slate-600">
                                <Users className="w-4 h-4 inline mr-1.5" />
                                {members.length} members
                            </span>
                            {canEdit && (
                                <button
                                    onClick={() => setShowAddMember(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors"
                                >
                                    <UserPlus className="w-4 h-4" />
                                    Add Member
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Permission Warning */}
            {!canEdit && (
                <div className="px-6 py-4">
                    <div className="max-w-5xl mx-auto">
                        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                            <AlertCircle className="w-4 h-4" />
                            Only organization <strong className="mx-1">admins</strong> and <strong className="mx-1">owners</strong> can manage group members.
                        </div>
                    </div>
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="px-6 py-4">
                    <div className="max-w-5xl mx-auto">
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="px-6 py-6">
                <div className="max-w-5xl mx-auto">
                    <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-purple-500" />
                            Group Members
                        </h3>
                        <MembersList
                            organization={organization}
                            workspace={workspace}
                            group={group}
                            members={members}
                            userEmail={userEmail}
                            onRefresh={loadData}
                            showInviteForm={false}
                            context="group"
                            compact={true}
                        />
                    </div>

                    {/* Group Settings */}
                    <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 mt-6">
                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
                            <Settings className="w-5 h-5 text-slate-500" />
                            Group Settings
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-medium dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                                    Group ID
                                </label>
                                <p className="dark:text-slate-400 text-slate-600 font-mono text-sm mt-1">
                                    {group.id}
                                </p>
                            </div>
                            <div>
                                <label className="text-xs font-medium dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                                    Workspace
                                </label>
                                <p className="dark:text-white text-slate-900 font-medium mt-1">
                                    {workspace.name}
                                </p>
                            </div>
                            <div>
                                <label className="text-xs font-medium dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                                    Created
                                </label>
                                <p className="dark:text-white text-slate-900 font-medium mt-1">
                                    {group.created_at
                                        ? new Date(group.created_at).toLocaleDateString()
                                        : "—"}
                                </p>
                            </div>
                            <div>
                                <label className="text-xs font-medium dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                                    Member Count
                                </label>
                                <p className="dark:text-white text-slate-900 font-medium mt-1">
                                    {members.length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Member Modal */}
            {showAddMember && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-2xl">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-bold dark:text-white text-slate-900">
                                    Add Member to Group
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowAddMember(false);
                                        setNewMemberEmail("");
                                        setAddError("");
                                    }}
                                    className="p-2 dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handleAddMember}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                        Email Address
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 dark:text-slate-500 text-slate-400" />
                                        <input
                                            type="email"
                                            placeholder="colleague@example.com"
                                            value={newMemberEmail}
                                            onChange={(e) => setNewMemberEmail(e.target.value)}
                                            className="w-full pl-11 pr-4 py-3 dark:bg-slate-950 bg-slate-50 border dark:border-slate-700 border-slate-300 rounded-xl text-sm dark:text-white text-slate-900 focus:ring-2 focus:ring-teal-500 outline-none"
                                            autoFocus
                                        />
                                    </div>
                                    <p className="mt-2 text-xs dark:text-slate-500 text-slate-400">
                                        The user must be a member of the organization to be added to this group.
                                    </p>
                                </div>

                                {addError && (
                                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        {addError}
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowAddMember(false);
                                            setNewMemberEmail("");
                                            setAddError("");
                                        }}
                                        className="flex-1 px-4 py-2.5 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isAdding || !newMemberEmail.trim()}
                                        className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isAdding ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <UserPlus className="w-4 h-4" />
                                                Add Member
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GroupDetail;
